import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAndroidNative } from '../mobile/capacitorPlatform';
import { downloadLatestAndroidApk, getLatestAndroidApkInfo } from '../api/clients/mobileAppClient';
import { Preferences } from '@capacitor/preferences';
import { App as CapacitorApp } from '@capacitor/app';
import { useToast } from '../ui/ToastProvider';

const SEEN_APK_FILENAME_KEY = 'react_android_apk_seen_filename';
const configuredInterval = Number(import.meta.env.VITE_ANDROID_UPDATE_CHECK_MS || 900000);
const checkInterval = Number.isFinite(configuredInterval) && configuredInterval >= 120000 ? configuredInterval : 900000;

function isAndroidUserAgent() {
    if (typeof navigator === 'undefined') {
        return false;
    }

    return /android/i.test(navigator.userAgent || '');
}

function resolveDownloadUrl(info) {
    if (!info) {
        return null;
    }

    if (info.downloadUrl) {
        return info.downloadUrl;
    }

    if (info.directFileUrl) {
        return info.directFileUrl;
    }

    return '/mobile-app/android/latest/download';
}

export default function AndroidUpdateControl({ token = null, onUpdateInfoChange = null }) {
    const toast = useToast();
    const [loading, setLoading] = useState(false);
    const [latest, setLatest] = useState(null);
    const [error, setError] = useState(null);
    const [seenFilename, setSeenFilename] = useState('');
    const [installedVersion, setInstalledVersion] = useState(null);
    const active = useRef(false);
    const accountToken = useRef(token);
    accountToken.current = token;

    const showControl = isAndroidNative() || isAndroidUserAgent();

    const readSeenFilename = useCallback(async () => {
        if (isAndroidNative()) {
            const result = await Preferences.get({ key: SEEN_APK_FILENAME_KEY });
            return result?.value || '';
        }

        try {
            return localStorage.getItem(SEEN_APK_FILENAME_KEY) || '';
        } catch {
            return '';
        }
    }, []);

    const writeSeenFilename = useCallback(async (value) => {
        if (!value) {
            return;
        }

        if (isAndroidNative()) {
            await Preferences.set({ key: SEEN_APK_FILENAME_KEY, value: String(value) });
            return;
        }

        try {
            localStorage.setItem(SEEN_APK_FILENAME_KEY, String(value));
        } catch {
            // noop
        }
    }, []);

    const refreshInstalledVersion = useCallback(async () => {
        if (!isAndroidNative()) {
            setInstalledVersion(null);
            return null;
        }

        try {
            const info = await CapacitorApp.getInfo();
            const version = info?.version || null;
            if (active.current) setInstalledVersion(version);
            return version;
        } catch {
            if (active.current) setInstalledVersion(null);
            return null;
        }
    }, []);

    const refresh = useCallback(async () => {
        if (!active.current || !showControl || !token || !navigator.onLine || document.visibilityState === 'hidden') {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const currentVersion = await refreshInstalledVersion();
            if (!active.current || accountToken.current !== token) return;
            const data = await getLatestAndroidApkInfo({ currentVersion, token, maxAgeMs: checkInterval });
            if (!active.current || accountToken.current !== token) return;
            if (data?.available) {
                setLatest(data);
            } else {
                setLatest(null);
            }
        } catch (requestError) {
            if (active.current && accountToken.current === token) setError(requestError);
        } finally {
            if (active.current && accountToken.current === token) setLoading(false);
        }
    }, [refreshInstalledVersion, showControl, token]);

    useEffect(() => {
        let disposed = false;
        active.current = true;

        const init = async () => {
            const seen = await readSeenFilename().catch(() => '');
            if (!disposed) {
                setSeenFilename(seen);
            }

            if (!disposed) {
                void refresh();
            }
        };

        void init();

        const timer = window.setInterval(() => {
            void refresh();
        }, checkInterval);

        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                void refresh();
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('online', onVisibility);

        let appListener = null;
        if (isAndroidNative()) {
            CapacitorApp.addListener('appStateChange', ({ isActive }) => {
                if (isActive) {
                    void refresh();
                }
            }).then((listener) => {
                if (disposed) void listener.remove();
                else appListener = listener;
            }).catch(() => {
                appListener = null;
            });
        }

        return () => {
            disposed = true;
            active.current = false;
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('online', onVisibility);
            if (appListener) {
                appListener.remove();
            }
        };
    }, [readSeenFilename, refresh, refreshInstalledVersion]);

    const hasNewVersion = useMemo(() => {
        if (!latest?.filename) {
            return false;
        }

        // Si la versión instalada ya coincide exactamente con la última disponible, no mostrar banner de actualización
        if (installedVersion && latest?.version) {
            const cleanInstalled = String(installedVersion).replace(/^v/i, '').trim();
            const cleanLatest = String(latest.version).replace(/^v/i, '').trim();
            if (cleanInstalled === cleanLatest) {
                return false;
            }
        }

        // Si el usuario ya vio/desestimó este archivo o versión
        return String(latest.filename) !== String(seenFilename || '');
    }, [latest?.filename, latest?.version, installedVersion, seenFilename]);

    const isForcedUpdate = Boolean(latest?.update?.required);
    const notifiedFilenameRef = useRef('');

    // Notificar al usuario cuando hay una nueva versión disponible sin ensuciar la cabecera
    useEffect(() => {
        if (hasNewVersion && latest?.filename && notifiedFilenameRef.current !== latest.filename) {
            notifiedFilenameRef.current = latest.filename;
            toast.info(`Nueva versión de la app disponible (${latest.version || latest.filename}).`);
        }
    }, [hasNewVersion, latest?.filename, latest?.version, toast]);

    const handleDownload = async () => {
        if (!token) {
            return;
        }

        const directUrl = latest?.directFileUrl || latest?.downloadUrl || resolveDownloadUrl(latest);

        // En Android (Capacitor o navegador móvil): abrir en el navegador del sistema para que descargue el APK y muestre la notificación nativa para instalarlo
        if (isAndroidNative() || isAndroidUserAgent()) {
            if (directUrl) {
                window.open(directUrl, '_system');
                toast.success('Descarga de APK iniciada.');
                if (latest?.filename) {
                    void writeSeenFilename(String(latest.filename));
                    setSeenFilename(String(latest.filename));
                }
                return;
            }
        }

        try {
            const { blob, filename } = await downloadLatestAndroidApk(token);
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = filename || 'app-android.apk';
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
            toast.success('Descarga de APK iniciada.');
        } catch {
            if (directUrl) {
                window.open(directUrl, '_system');
                toast.success('Descarga de APK iniciada.');
            }
        }

        if (latest?.filename) {
            void writeSeenFilename(String(latest.filename));
            setSeenFilename(String(latest.filename));
        }
    };

    const handleDismiss = useCallback(async () => {
        if (latest?.filename) {
            await writeSeenFilename(String(latest.filename));
            setSeenFilename(String(latest.filename));
        }
    }, [latest?.filename, writeSeenFilename]);

    useEffect(() => {
        if (typeof onUpdateInfoChange === 'function') {
            onUpdateInfoChange({
                hasNewVersion,
                latest,
                loading,
                handleDownload,
                handleDismiss,
                isForcedUpdate,
            });
        }
    }, [hasNewVersion, latest, loading, handleDismiss, isForcedUpdate, onUpdateInfoChange]);

    // No renderizar nada en la cabecera (evita botones vacíos). Solo mostrar modal si es obligatoria.
    if (!isForcedUpdate) {
        return null;
    }

    return (
        <div className="react-shell-update-required-backdrop">
            <div className="react-shell-update-required-modal" role="dialog" aria-modal="true" aria-label="Actualizacion obligatoria">
                <h3>Actualización obligatoria</h3>
                <p>
                    Debes actualizar la aplicación para continuar.
                    {latest?.minSupportedVersion ? ` Mínimo requerido: ${latest.minSupportedVersion}.` : ''}
                </p>
                <button
                    type="button"
                    className="react-shell-update-required-btn"
                    onClick={handleDownload}
                    disabled={loading || !latest}
                >
                    Descargar actualización
                </button>
            </div>
        </div>
    );
}
